/**
 * ADR-118 — Django-allauth headless adapter.
 *
 * Covers ONE (`apps/aegix-one-react`). Wraps the allauth headless
 * headless app API at `/_allauth/app/v1/auth/*`. Supports:
 *   - email + password login
 *   - social login (Google / Apple / Microsoft) via either the
 *     popup ID-token flow or the redirect-callback flow
 *   - passkey / WebAuthn via `navigator.credentials.get(...)`
 *   - self-registration with email verification
 *
 * ### JWT and session token transport (app-client alignment)
 *
 * Stock django-allauth's headless mode supports both cookie-based session
 * transport (browser client) and header-based session token transport (app client).
 * Aegix One uses the app-client endpoints at `/_allauth/app/v1/auth/*` to support
 * cross-origin deployments (mitigating third-party cookie blocking).
 *
 * The adapter tracks the django-allauth session token via:
 *   - The `X-Session-Token` header.
 *   - Any response's `meta.session_token` is captured and echoed on subsequent calls.
 *   - On page reloads, the session identifier is automatically extracted from the JWT
 *     claims (stored in `StorageKeys.token`) and passed back as the `X-Session-Token`.
 *
 * Aegix layers a JWT-issuing token strategy on top (`JWTTokenStrategy`).
 * The `LoginResult.jwt` carries the real token.
 */

import type { BaseUser, LoginResult, LogoutResult } from '@aegix/auth-core';
import {
  AuthError,
  type LoginAdapter,
  type LoginCredentials,
  type OAuthCredentials,
  type SignupCredentials,
  type RequiresVerification,
} from './types';

export interface AllauthAdapterConfig {
  /**
   * Base URL for the backend. Defaults to empty (relative URLs).
   */
  baseUrl?: string;
  /**
   * `fetch` implementation. Defaults to `globalThis.fetch`. Tests
   * inject a mock.
   */
  fetchImpl?: typeof fetch;
  /**
   * `navigator.credentials` accessor. Defaults to the global
   * `navigator.credentials`. Tests inject a mock for the passkey
   * ceremony.
   */
  credentialsContainer?: CredentialsContainer | null;
  /**
   * Whether to use token-based transport (X-Session-Token and meta.session_token)
   * via the allauth /app/v1 API endpoints. If false, cookie-based transport
   * via the /browser/v1 API is used instead.
   */
  useTokenTransport?: boolean;
}

/**
 * The user shape ONE uses today. 4 fields. The lib is generic on
 * `U extends BaseUser`; this is the typical shape consumers will use.
 */
export interface AllauthUser extends BaseUser {
  first_name: string;
  last_name: string;
}

/**
 * Sentinel value placed in `LoginResult.jwt` when the allauth backend
 * does not surface a JWT (e.g. stock allauth without a JWT-issuing
 * token strategy). The real auth material is the allauth `sessionid`
 * cookie; this sentinel exists so `libs/auth-core`'s state machine
 * has a non-empty `jwt` field for `authenticated` states. Consumer
 * code MUST NOT use this value as a Bearer token.
 *
 * Backends that DO issue JWTs (e.g. Aegix's `JWTTokenStrategy`) cause
 * `LoginResult.jwt` to carry the real token instead — see the file
 * header for the dual-mode rationale.
 */
export const ALLAUTH_SESSION_SENTINEL = '__session__';

/**
 * Extract the access-token JWT from an allauth response body, falling
 * back to the session sentinel when the backend does not issue one.
 * Centralized so every completion path (login, signup, OAuth,
 * passkey) treats the JWT field identically.
 */
function extractAccessToken(data: unknown): string {
  const token = (data as { meta?: { access_token?: string } } | null)?.meta
    ?.access_token;
  return typeof token === 'string' && token.length > 0
    ? token
    : ALLAUTH_SESSION_SENTINEL;
}

/**
 * Extract the refresh-token JWT from an allauth response body, or
 * `undefined` when the backend doesn't include one. Aegix's
 * `JWTTokenStrategy.create_refresh_token` surfaces this as
 * `meta.refresh_token`; backends without a refresh-issuing strategy
 * omit it, which is fine — boot-time refresh just won't run.
 *
 * Centralized for the same reason as `extractAccessToken`: every
 * completion path treats the field identically and a future allauth
 * field rename is one edit.
 */
function extractRefreshToken(data: unknown): string | undefined {
  const token = (data as { meta?: { refresh_token?: string } } | null)?.meta
    ?.refresh_token;
  return typeof token === 'string' && token.length > 0 ? token : undefined;
}

/**
 * Extract the session key claim from a JWT.
 */
function extractSessionKeyFromJwt(token: string | null | undefined): string | null {
  if (!token || token === ALLAUTH_SESSION_SENTINEL) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payloadJson = atob(base64);
    const payload = JSON.parse(payloadJson);
    return payload.session_key || null;
  } catch {
    return null;
  }
}

// Shared token cache across the adapter and any app-level custom allauth requests
let sharedSessionToken: string | null = null;

export function getSharedSessionToken(): string | null {
  return sharedSessionToken;
}

export function setSharedSessionToken(token: string | null): void {
  sharedSessionToken = token;
}

export function clearSharedSessionToken(): void {
  sharedSessionToken = null;
}

export function createAllauthAdapter<U extends BaseUser = AllauthUser>(
  config: AllauthAdapterConfig = {},
): LoginAdapter<U> {
  const fetchImpl = config.fetchImpl ?? globalThis.fetch;
  const baseUrl = config.baseUrl ?? '';
  const credentialsContainer =
    config.credentialsContainer ??
    (typeof navigator !== 'undefined' ? navigator.credentials : null);

  const pathPrefix = config.useTokenTransport ? '/_allauth/app/v1' : '/_allauth/browser/v1';
  function getSessionToken(jwtToken?: string | null): string | null {
    if (!config.useTokenTransport) return null;
    const token = getSharedSessionToken();
    if (token) return token;
    const extracted = extractSessionKeyFromJwt(jwtToken);
    if (extracted) {
      setSharedSessionToken(extracted);
    }
    return extracted;
  }

  function getRequestHeaders(
    jwtToken?: string | null,
    extraHeaders?: Record<string, string>,
  ): Record<string, string> {
    const headers: Record<string, string> = { ...extraHeaders };
    const sessionToken = getSessionToken(jwtToken);
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }
    return headers;
  }

  function handleResponseData(data: any) {
    if (!config.useTokenTransport) return;
    if (data?.meta?.session_token) {
      setSharedSessionToken(data.meta.session_token);
    } else if (data?.meta?.access_token) {
      clearSharedSessionToken();
    }
  }

  return {
    async login(credentials: LoginCredentials): Promise<LoginResult<U>> {
      const response = await fetchImpl(
        `${baseUrl}${pathPrefix}/auth/login`,
        {
          method: 'POST',
          headers: getRequestHeaders(null, { 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({
            email: credentials.email,
            password: credentials.password,
          }),
        },
      );

      const data = (await safeJson(response)) as
        | { status: number; data?: { user: U }; meta?: { is_authenticated?: boolean, session_token?: string, access_token?: string, refresh_token?: string } }
        | null;

      handleResponseData(data);

      // allauth's headless API returns 401 for "valid credentials,
      // but extra steps required" (e.g. email verification). Check
      // for the `user` field in the body to distinguish.
      if (!response.ok && !data?.data?.user) {
        throwAllauthError(response, data);
      }

      if (!data?.data?.user) {
        // Successful response but no user — typically means email
        // verification is pending. The caller flow on ONE branches
        // on this case via `signup` rather than `login`; if `login`
        // hits it, escalate as `requires_verification`.
        throw new AuthError(
          'Email verification required before login completes',
          'requires_verification',
          data,
        );
      }

      return {
        user: data.data.user,
        jwt: extractAccessToken(data),
        refreshToken: extractRefreshToken(data),
      };
    },

    async loginWithOAuth(
      credentials: OAuthCredentials,
    ): Promise<LoginResult<U>> {
      // Dispatch on which flow the caller is using:
      //   - `idToken` (+ optional `nonce`): popup/embedded ID-token
      //     flow, where the SPA already holds the platform token.
      //   - `params`: redirect-callback flow, where the SPA forwards
      //     the full query-string dict returned by the provider's
      //     redirect (allauth's `/auth/provider/token` endpoint
      //     accepts the dict verbatim as the `token` field).
      // Exactly one must be present; checked here to avoid silent
      // server-side coercion to an empty token.
      if (!credentials.idToken && !credentials.params) {
        throw new AuthError(
          'loginWithOAuth requires either `idToken` (popup flow) or `params` (redirect flow)',
          'unknown',
        );
      }
      const token: Record<string, unknown> = credentials.idToken
        ? {
            id_token: credentials.idToken,
            ...(credentials.nonce ? { nonce: credentials.nonce } : {}),
          }
        : (credentials.params as Record<string, unknown>);

      const response = await fetchImpl(
        `${baseUrl}${pathPrefix}/auth/provider/token`,
        {
          method: 'POST',
          headers: getRequestHeaders(null, { 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({
            provider: credentials.provider,
            token,
          }),
        },
      );

      const data = (await safeJson(response)) as
        | { status: number; data?: { user: U }; meta?: { session_token?: string, access_token?: string, refresh_token?: string } }
        | null;

      handleResponseData(data);

      if (!response.ok || !data?.data?.user) {
        throwAllauthError(response, data);
      }

      return {
        user: data.data.user,
        jwt: extractAccessToken(data),
        refreshToken: extractRefreshToken(data),
      };
    },

    async loginWithPasskey(): Promise<LoginResult<U>> {
      if (!credentialsContainer) {
        throw new AuthError(
          'WebAuthn not available in this environment',
          'unknown',
        );
      }

      // 1. Begin: fetch challenge from allauth
      const beginResp = await fetchImpl(
        `${baseUrl}${pathPrefix}/auth/webauthn/login`,
        {
          method: 'GET',
          headers: getRequestHeaders(),
          credentials: 'include',
        },
      );
      const beginBody = (await safeJson(beginResp)) as
        | { data?: { request_options: PublicKeyCredentialRequestOptionsJSON }; meta?: { session_token?: string } }
        | null;

      handleResponseData(beginBody);

      if (!beginResp.ok || !beginBody?.data?.request_options) {
        throwAllauthError(beginResp, beginBody);
      }

      // 2. Run the WebAuthn ceremony
      const assertion = await credentialsContainer.get({
        publicKey: parseRequestOptions(
          (beginBody as { data: { request_options: PublicKeyCredentialRequestOptionsJSON } }).data.request_options,
        ),
      });
      if (!assertion) {
        throw new AuthError(
          'Passkey ceremony cancelled or unavailable',
          'unknown',
        );
      }

      // 3. Complete: post the assertion back
      const completeResp = await fetchImpl(
        `${baseUrl}${pathPrefix}/auth/webauthn/login`,
        {
          method: 'POST',
          headers: getRequestHeaders(null, { 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({
            credential: serializeAssertion(assertion),
          }),
        },
      );

      const data = (await safeJson(completeResp)) as
        | { data?: { user: U }; meta?: { session_token?: string, access_token?: string, refresh_token?: string } }
        | null;

      handleResponseData(data);

      if (!completeResp.ok || !data?.data?.user) {
        throwAllauthError(completeResp, data);
      }
      return {
        user: data.data.user,
        jwt: extractAccessToken(data),
        refreshToken: extractRefreshToken(data),
      };
    },

    async signup(
      credentials: SignupCredentials,
    ): Promise<LoginResult<U> | RequiresVerification> {
      const response = await fetchImpl(
        `${baseUrl}${pathPrefix}/auth/signup`,
        {
          method: 'POST',
          headers: getRequestHeaders(null, { 'Content-Type': 'application/json' }),
          credentials: 'include',
          body: JSON.stringify({
            email: credentials.email,
            password: credentials.password,
            ...(credentials.first_name
              ? { first_name: credentials.first_name }
              : {}),
            ...(credentials.last_name
              ? { last_name: credentials.last_name }
              : {}),
          }),
        },
      );

      const data = (await safeJson(response)) as
        | { status: number; data?: { user?: U }; meta?: { is_authenticated?: boolean, session_token?: string, access_token?: string, refresh_token?: string } }
        | null;

      handleResponseData(data);

      // 401 with no user means "account created, please verify email"
      if (response.status === 401 && !data?.data?.user) {
        return {
          requiresVerification: true,
          email: credentials.email,
        };
      }

      if (!response.ok || !data?.data?.user) {
        throwAllauthError(response, data);
      }

      return {
        user: data.data.user,
        jwt: extractAccessToken(data),
        refreshToken: extractRefreshToken(data),
      };
    },

    async logout(jwt: string | null): Promise<LogoutResult> {
      // allauth's headless DELETE on the session endpoint revokes
      // the current session token.
      try {
        const response = await fetchImpl(
          `${baseUrl}${pathPrefix}/auth/session`,
          {
            method: 'DELETE',
            headers: getRequestHeaders(jwt),
            credentials: 'include',
          },
        );

        // 401 means "already logged out" — fine.
        if (!response.ok && response.status !== 401) {
          throw new AuthError(
            `allauth logout failed with status ${response.status}`,
            response.status >= 500 ? 'server' : 'unknown',
            await safeJson(response),
          );
        }
        return { ok: true };
      } finally {
        clearSharedSessionToken();
      }
    },

    async fetchMe(token?: string | null): Promise<U> {
      // Boot reconcile probe for allauth: we extract the session token
      // from the stored JWT token and propagate it via the `X-Session-Token`
      // header (or use an in-memory tracked token if available).
      const response = await fetchImpl(
        `${baseUrl}${pathPrefix}/auth/session`,
        {
          method: 'GET',
          headers: getRequestHeaders(token),
          credentials: 'include',
        },
      );
      const data = (await safeJson(response)) as
        | { data?: { user: U }; meta?: { session_token?: string } }
        | null;

      handleResponseData(data);

      if (!response.ok || !data?.data?.user) {
        throw new AuthError(
          `allauth session probe failed with status ${response.status}`,
          response.status === 401 ? 'invalid_credentials' : 'server',
          data,
        );
      }
      return data.data.user;
    },

    async updateLanguage(
      userId: string | number,
      language: string,
      token?: string | null,
    ): Promise<U> {
      // This is the one adapter method that leaves the `/_allauth/*`
      // family, so `getRequestHeaders()` is the wrong tool — CloudFront only
      // forwards `X-Session-Token` on `/_allauth/*`, and `/api/v2/*` wants the
      // JWT anyway. Send it the way `simple-jwt.ts` does.
      //
      // Under the browser client the `sessionid` cookie carried this request;
      // under the app client there is no such cookie, so cookie-only auth
      // 401s. `credentials: 'include'` stays for cookie-transport envs.
      //
      // Scheme is `JWT`, not `Bearer` — SIMPLE_JWT AUTH_HEADER_TYPES = ("JWT",).
      // The sentinel guard matters here (and not in `simple-jwt.ts`) because
      // `LoginResult.jwt` is `ALLAUTH_SESSION_SENTINEL` when the backend
      // issues no JWT; see its docstring — it must never be sent as a
      // credential.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token && token !== ALLAUTH_SESSION_SENTINEL) {
        headers['Authorization'] = `JWT ${token}`;
      }
      const response = await fetchImpl(
        `${baseUrl}/api/v2/user/${userId}/`,
        {
          method: 'PATCH',
          headers,
          credentials: 'include',
          body: JSON.stringify({ language }),
        },
      );
      const data = (await safeJson(response)) as U | null;
      if (!response.ok || !data) {
        throw new AuthError(
          `Failed to update language (status ${response.status})`,
          response.status === 401 ? 'invalid_credentials' : 'server',
          data,
        );
      }
      return data;
    },
  };
}

// ---- WebAuthn helpers ----

/**
 * allauth returns the WebAuthn challenge as base64url-encoded JSON.
 * Convert to ArrayBuffer fields the browser API expects.
 */
interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  rpId?: string;
  timeout?: number;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: Array<{
    id: string;
    type: 'public-key';
    transports?: AuthenticatorTransport[];
  }>;
}

function parseRequestOptions(
  json: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  return {
    challenge: base64urlToBuffer(json.challenge),
    rpId: json.rpId,
    timeout: json.timeout,
    userVerification: json.userVerification,
    allowCredentials: json.allowCredentials?.map((c) => ({
      id: base64urlToBuffer(c.id),
      type: c.type,
      transports: c.transports,
    })),
  };
}

function serializeAssertion(credential: Credential): unknown {
  const c = credential as PublicKeyCredential;
  const response = c.response as AuthenticatorAssertionResponse;
  return {
    id: c.id,
    rawId: bufferToBase64url(c.rawId),
    type: c.type,
    response: {
      authenticatorData: bufferToBase64url(response.authenticatorData),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle
        ? bufferToBase64url(response.userHandle)
        : null,
    },
  };
}

function base64urlToBuffer(b64url: string): ArrayBuffer {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(
    Math.ceil(b64url.length / 4) * 4,
    '=',
  );
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ---- error helpers ----

function throwAllauthError(
  response: Response,
  data: unknown,
): never {
  const detail =
    (data as { errors?: Array<{ message?: string }> })?.errors?.[0]
      ?.message ?? `allauth request failed (HTTP ${response.status})`;
  let kind: AuthError['kind'] = 'unknown';
  if (response.status === 401) kind = 'invalid_credentials';
  else if (response.status === 423) kind = 'account_locked';
  else if (response.status >= 500) kind = 'server';
  throw new AuthError(detail, kind, data);
}

async function safeJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
